import { csx, Flex, Text } from '@vtex/admin-ui'
import { useDrawerSubForm } from 'hooks'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { DiscountType, ItemPriceRange } from 'typings'
import {
  CancelButton,
  CurrencyInput,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../..'
import { ItemsReferencePriceAlert } from '../shared/ItemsReferencePriceAlert'
import { messages } from './messages'
import { validationSuite } from './validation'

const EMPTY_CONFIG = Object.freeze({
  min: undefined,
  max: undefined,
})

function isRange(range: Partial<ItemPriceRange>): range is ItemPriceRange {
  return range.min != undefined || range.max != undefined
}

interface Props {
  state: UseDrawerStateReturn
  initialConfig: ItemPriceRange | undefined
  discountType: DiscountType | undefined
  onSave: (config: ItemPriceRange | undefined) => void
}

export function Editor(props: Props) {
  const intl = useIntl()
  const { $t } = intl
  const { state, onSave, initialConfig, discountType } = props
  const { hide } = state

  const config = useMemo(
    () => initialConfig ?? { ...EMPTY_CONFIG },
    [initialConfig]
  )

  const { drawerForm } = useDrawerSubForm<Partial<ItemPriceRange>>({
    config,
    isOpen: state.open,
    validationFn: validationSuite(intl),
  })

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()

    if (isValid) {
      const values = drawerForm.getValues()
      onSave(isRange(values) ? values : undefined)
      hide()
    }
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.editor.title)}</DrawerTitle>
        {(!discountType || discountType === DiscountType.Price) && (
          <DrawerSubtitle>
            <FormattedMessage
              {...messages.editor.subtitle}
              values={{
                b: (content) => <Text variant="action1">{content}</Text>,
              }}
            />
          </DrawerSubtitle>
        )}
        <ItemsReferencePriceAlert discountType={discountType} mode="item" />
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Flex className={csx({ columnGap: '$space-5', '>div': { flex: 1 } })}>
          <CurrencyInput
            name="min"
            label={$t(messages.editor.min)}
            state={drawerForm}
          />
          <CurrencyInput
            name="max"
            label={$t(messages.editor.max)}
            state={drawerForm}
          />
        </Flex>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={!initialConfig} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
